import { RRule } from "verdi-common/utils/RRuleLibrary";
import { IrrigationEventResponse } from "@/api/orval/generated/schemas";

/**
 * What expansion needs from a definition: the stored event DTO satisfies it, and so does the
 * in-flight editing draft.
 */
export type ExpandableIrrigationDefinition = Pick<
    IrrigationEventResponse,
    | "recordId"
    | "rrule"
    | "zonesActive"
    | "defaultDuration"
    | "zoneIrrigationOffsets"
    | "zoneWateringPercentages"
    | "irrigationSet"
> & {
    /**
     * Required on a stored event, absent on the editing draft: a ghost bar has no saved version, so
     * this is optional here even though the response contract requires it.
     */
    versionId?: number;
};

export type IrrigationInstance = {
    recordId: NonNullable<IrrigationEventResponse["recordId"]>;
    /**
     * The version of the definition this occurrence was expanded from, which an edit sends as the
     * version it was made against. A refetch replaces it. Absent on a draft's ghost bars, which have
     * no saved version yet.
     */
    versionId?: number;
    instanceKey: string;
    zoneId: string;
    irrigationSetId?: string;
    startTime: Date;
    durationMs: number;
    occurrence: Date;
};

/**
 * How far before the window we have to start walking this definition's RRULE.
 *
 * A zone can start days after the occurrence (set chunk offsets) and then run for its own duration, so an
 * occurrence well before the window can still paint a bar inside it. Walk back by the largest offset plus
 * the longest zone duration this definition can produce.
 */
function getLookbackMs({
    defaultDuration,
    zoneIrrigationOffsets,
    zoneWateringPercentages,
}: ExpandableIrrigationDefinition) {
    const maxOffsetMs = Math.max(0, ...Object.values(zoneIrrigationOffsets ?? {}));
    // Floor at 1 so a definition with no TIP percentages still reserves the full default duration.
    const maxDurationScale = Math.max(1, ...Object.values(zoneWateringPercentages ?? {}));
    return maxOffsetMs + defaultDuration * maxDurationScale;
}

function getOccurrences(rrule: string, recordId: string | undefined, start: Date, end: Date): Date[] | undefined {
    try {
        return RRule.fromString(rrule).between(start, end);
    } catch (error) {
        // Bail for a single invalid RRULE definition, so we don't take down the entire scheduler
        console.error(`Skipping irrigation event ${recordId}: could not read rrule ${JSON.stringify(rrule)}`, error);
        return undefined;
    }
}

function expandDefinition(
    irrigationEventDefinition: ExpandableIrrigationDefinition,
    windowStart: Date,
    end: Date,
    wantedZoneIds: ReadonlySet<string> | undefined,
) {
    const {
        recordId,
        versionId,
        rrule,
        zonesActive,
        defaultDuration,
        zoneIrrigationOffsets,
        zoneWateringPercentages,
        irrigationSet,
    } = irrigationEventDefinition;

    if (!Array.isArray(zonesActive) || zonesActive.length === 0) {
        // This is a bug on the backend if it happens, so just bail out
        return [];
    }

    const zonesToExpand = wantedZoneIds ? zonesActive.filter((zoneId) => wantedZoneIds.has(zoneId)) : zonesActive;
    if (zonesToExpand.length === 0) {
        return [];
    }

    const expandStart = new Date(windowStart.valueOf() - getLookbackMs(irrigationEventDefinition));
    const occurrences = getOccurrences(rrule, recordId, expandStart, end);
    if (!occurrences) {
        return [];
    }

    return zonesToExpand.flatMap((zoneId) =>
        occurrences.map(
            (occurrence) =>
                ({
                    recordId,
                    versionId,
                    instanceKey: `${recordId}:${zoneId}:${occurrence.valueOf()}`,
                    zoneId,

                    occurrence,

                    // This is to account for the set editor's offset setting
                    startTime: new Date(occurrence.valueOf() + (zoneIrrigationOffsets?.[zoneId] ?? 0)),

                    // This is the TIP adjustment
                    durationMs: defaultDuration * (zoneWateringPercentages?.[zoneId] ?? 1),

                    irrigationSetId: irrigationSet,
                }) as IrrigationInstance,
        ),
    );
}

/**
 * Expand event definitions into per-zone, per-occurrence instances.
 *
 * `zoneIds` is a whitelist: leave it `undefined` to expand every active zone (what the scheduler
 * timeline wants), or pass the zones you actually paint. Passing an empty array means "no zones",
 * so nothing is expanded — that is the cheap path for a consumer that has no zone selected yet.
 *
 * Pass the plain window you want to paint; each definition walks back past `windowStart` as far as it
 * needs to (see {@link getLookbackMs}). Trim the overshoot with {@link filterInstancesOverlappingWindow}.
 */
export function expandIrrigationDefinitions(
    definitions: ExpandableIrrigationDefinition[],
    windowStart: Date,
    windowEnd: Date,
    zoneIds?: readonly string[],
) {
    const wantedZoneIds = zoneIds ? new Set(zoneIds) : undefined;
    if (wantedZoneIds?.size === 0) {
        return [];
    }

    // Definitions with no recordId are dropped on purpose: _id changes with each SCD version, so it
    // can't stand in as identity.
    return definitions
        .filter((definition) => typeof definition.recordId === "string")
        .flatMap((definition) => expandDefinition(definition, windowStart, windowEnd, wantedZoneIds));
}

export function groupInstancesByZoneId(instances: IrrigationInstance[]): Record<string, IrrigationInstance[]> {
    const byZoneId: Record<string, IrrigationInstance[]> = {};
    instances.forEach((instance) => {
        (byZoneId[instance.zoneId] ??= []).push(instance);
    });
    return byZoneId;
}

/** Standalone instances (no linked set) are dropped — only set rows read this. */
export function groupInstancesBySetId(instances: IrrigationInstance[]): Record<string, IrrigationInstance[]> {
    const bySetId: Record<string, IrrigationInstance[]> = {};
    instances.forEach((instance) => {
        if (!instance.irrigationSetId) {
            return;
        }
        (bySetId[instance.irrigationSetId] ??= []).push(instance);
    });
    return bySetId;
}

export function filterInstancesOverlappingWindow(instances: IrrigationInstance[], windowStart: Date, windowEnd: Date) {
    return instances.filter((instance) => {
        const startTimeMs = instance.startTime.valueOf();
        return startTimeMs + instance.durationMs >= windowStart.valueOf() && startTimeMs < windowEnd.valueOf();
    });
}
